import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  downloadArchivoRechazo,
  listArchivosRechazo,
  uploadArchivoRechazo,
} from "./ApiServices";
import {
  TIPOS_ARCHIVO_OPCIONES,
  TIPOS_ARCHIVO_RECHAZO,
  labelTipoArchivo,
} from "./chequesRechazos.constants";

const extraerLista = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.archivos)) return data.archivos;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const idArchivoDe = (row) =>
  row?.id ?? row?.id_archivo ?? row?.idArchivo ?? null;

const nombreArchivoDe = (row) =>
  row?.nombre ||
  row?.nombre_original ||
  row?.filename ||
  row?.archivo ||
  `archivo-${idArchivoDe(row) || ""}`;

const errorParaUsuario = (err, fallback) => {
  const msg = String(err?.message || "").trim();
  const lower = msg.toLowerCase();
  if (!msg) return fallback;
  if (lower.includes("unexpected field") || lower.includes("unexpected file")) {
    return "No se pudo subir el archivo. Probá de nuevo.";
  }
  if (lower.includes("file too large") || lower.includes("too large")) {
    return "El archivo es demasiado grande.";
  }
  if (/^(unexpected|forbidden|unauthorized|multipart|payload)/i.test(msg)) {
    return fallback;
  }
  return msg;
};

export default function ExpedienteArchivos({
  idRechazo,
  tipoDefault = TIPOS_ARCHIVO_RECHAZO.OTRO,
  disabled = false,
  compact = false,
  onChange,
}) {
  const inputRef = useRef(null);
  const [archivos, setArchivos] = useState([]);
  const [tipo, setTipo] = useState(tipoDefault);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setTipo(tipoDefault);
  }, [tipoDefault]);

  const cargar = useCallback(async () => {
    if (!idRechazo) {
      setArchivos([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await listArchivosRechazo(idRechazo);
      setArchivos(extraerLista(data));
    } catch (err) {
      setError(errorParaUsuario(err, "No se pudieron listar los archivos"));
      setArchivos([]);
    } finally {
      setLoading(false);
    }
  }, [idRechazo]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const subir = async (file) => {
    if (!file || !idRechazo || disabled) return;
    setSubiendo(true);
    setError("");
    try {
      await uploadArchivoRechazo(idRechazo, file, tipo);
      await cargar();
      onChange?.();
    } catch (err) {
      setError(errorParaUsuario(err, "No se pudo subir el archivo"));
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const descargar = async (row) => {
    const idArchivo = idArchivoDe(row);
    if (!idArchivo || !idRechazo) return;
    setDescargando(idArchivo);
    setError("");
    try {
      await downloadArchivoRechazo(idRechazo, idArchivo, nombreArchivoDe(row));
    } catch (err) {
      setError(errorParaUsuario(err, "No se pudo descargar el archivo"));
    } finally {
      setDescargando(null);
    }
  };

  if (!idRechazo) {
    return (
      <Alert severity="info">
        El expediente de archivos se habilita al crear el caso de rechazo.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={600}>
        Expediente digital
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!disabled ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
        >
          <TextField
            select
            size="small"
            label="Tipo (opcional)"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            sx={{ minWidth: 180 }}
            disabled={subiendo}
          >
            {TIPOS_ARCHIVO_OPCIONES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <input
            ref={inputRef}
            type="file"
            hidden
            onChange={(e) => subir(e.target.files?.[0])}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={
              subiendo ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            disabled={subiendo}
            onClick={() => inputRef.current?.click()}
          >
            Subir archivo
          </Button>
        </Stack>
      ) : null}
      {!disabled ? (
        <Typography variant="caption" color="text.secondary">
          Al subir, el archivo * es obligatorio. El tipo es opcional (default: otro).
        </Typography>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={20} />
        </Box>
      ) : archivos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay archivos en el expediente.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {archivos.map((row, idx) => {
            const idArchivo = idArchivoDe(row);
            return (
              <Stack
                key={idArchivo || idx}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 1,
                  py: compact ? 0.25 : 0.75,
                }}
              >
                <InsertDriveFileIcon fontSize="small" color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={nombreArchivoDe(row)}>
                    {nombreArchivoDe(row)}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={labelTipoArchivo(row?.tipo)}
                />
                <IconButton
                  size="small"
                  onClick={() => descargar(row)}
                  disabled={!idArchivo || descargando === idArchivo}
                  aria-label="Descargar"
                >
                  {descargando === idArchivo ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
